"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Eye, EyeOff } from "lucide-react";
import AlertMsg from "../common/AlertMsg";
import { changePasswordSchema } from "@/utils/schema/changePasswordSchema";
import { useChangePasswordMutation } from "@/store/slices/apiSlice";
import { SHOW_MESSAGE } from "@/lib/constant/constant";

export default function ChangePasswordDialog({
  isResetPasswordOpen,
  setIsResetPasswordOpen,
  userDetails,
}) {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [alertMsg, setAlertMsg] = useState({
    open: false,
    label: "",
    msg: "",
    isError: false,
    isLoading: false,
    btnText: "",
    canClose: false,
  });

  // Zod validation
  const form = useForm({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      email: userDetails?.email || "",
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Handel close alert
  const handleAlertClose = () => {
    setAlertMsg({
      open: false,
      label: "",
      msg: "",
      isError: false,
      isLoading: false,
      btnText: "",
      canClose: false,
    });

    if (!alertMsg?.isError) {
      setIsResetPasswordOpen(false);
      form.reset();
    }
  };

  // Submit change password function
  const onSubmit = async (data) => {
    try {
      let payload = {
        // email: userDetails?.email,
        currentPassword: data.oldPassword,
        newPassword: data.newPassword,
      };

      const res = await changePassword(payload).unwrap();
      // const res = { response: { statusEnum: 1 } };
      if (res?.response.statusEnum === 1) {
        setAlertMsg({
          open: true,
          label: SHOW_MESSAGE?.CHANGE_PASSWORD_SUCCESS_HEADING,
          msg: SHOW_MESSAGE?.CHANGE_PASSWORD_SUCCESS_MSG,
          isError: false,
          btnText: "Close",
          canClose: false,
        });
      } else {
        if (res?.response.errorCode === 164) {
          res?.response.data.sets.forEach((err) => {
            setAlertMsg({
              open: true,
              label: SHOW_MESSAGE?.VALIDATION_FAILED,
              msg: err?.message || res?.response.message,
              isError: true,
              btnText: "Close",
              canClose: false,
            });
          });
        } else {
          setAlertMsg({
            open: true,
            label: SHOW_MESSAGE?.CHANGE_PASSWORD_FAILED,
            msg: res?.response.message,
            isError: true,
            btnText: "Close",
            canClose: false,
          });
        }
      }
    } catch (err) {
      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.CHANGE_PASSWORD_FAILED,
        msg: err?.data?.response?.message || SHOW_MESSAGE?.SOMETHING_WENT_WRONG,
        isError: true,
        btnText: "Close",
        canClose: false,
      });
      console.error("Change password Error:", err);
    }
  };

  return (
    <>
      <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
        <DialogContent className="sm:max-w-md bg-brand-10 border-brand-11">
          <DialogHeader className="text-center">
            <DialogTitle className="text-2xl text-center font-semibold">
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-neutral-100 font-medium text-sm text-center !mt-2">
              Set your new password
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 py-4"
            >
              {/* Old Password */}
              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-responsive-sm font-medium">
                      Old Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showOldPassword ? "text" : "password"}
                          className="form-dark-input pr-10 bg-brand-10"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? (
                            <Eye className="h-4 w-4 text-primary" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* New Password */}
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-responsive-sm font-medium">
                      New Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? "text" : "password"}
                          className="form-dark-input pr-10 bg-brand-10"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <Eye className="h-4 w-4 text-primary" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Confirm Password */}
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-responsive-sm font-medium">
                      Confirm Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-dark-input pr-10 bg-brand-10"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? (
                            <Eye className="h-4 w-4 text-primary" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="pt-2 gap-3  sm:gap-3 flex-row !justify-center">
                {/* Clear (neutral) and Reset (default) to match your AddOffspring buttons */}
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="neutral"
                    onClick={() => form.reset()}
                    disabled={isLoading || form.formState.isSubmitting}
                    className="px-8"
                  >
                    Clear
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  variant="default"
                  className="px-8 bg-gradient-to-r from-[#188CEB] to-[#3758E8] hover:opacity-90"
                  disabled={isLoading || form.formState.isSubmitting}
                >
                  {isLoading || form.formState.isSubmitting
                    ? "Resetting..."
                    : "Reset"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </>
  );
}
